import { useEffect, useRef } from 'react';
import { realTimeChatCountStatusAtom } from '../../../states/chat';
import { useAtom, useAtomValue } from 'jotai';
import { ChatStatuses, RealtimeSearchCountResolution } from '../../../typings';
import { socketEventReceivedAtom } from '../../../states/message';
import useInfinitePolling from '../../../../../../shared/hooks/use-infinite-poll';
import { chatFilterStatusCountsAtom } from '../../../states/chat-list';

const POLLING_INTERVAL = 20 * 1000;

const useUpdateFilterCounts = ({
  refreshFilterCounts,
  realTimeCountStatus,
  searchTerm,
  liveChatCountsRefreshInterval,
  isTabVisible,
}: {
  refreshFilterCounts: () => void;
  realTimeCountStatus: boolean;
  searchTerm?: string;
  liveChatCountsRefreshInterval: number;
  isTabVisible: boolean;
}) => {
  const { data: realTimeChatCountStatus } = useAtomValue(realTimeChatCountStatusAtom);
  const [socketEventReceived, setSocketEventReceived] = useAtom(socketEventReceivedAtom);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const apiInProgressRef = useRef(false);
  const searchTermRef = useRef(searchTerm);

  useEffect(() => {
    searchTermRef.current = searchTerm;
  }, [searchTerm]);

  useEffect(() => {
    // Only act when socket event is received
    if (!socketEventReceived) return;

    // If timeout exists or API in progress, do nothing
    if (timeoutRef.current || apiInProgressRef.current) {
      return;
    }

    // If liveChatCountsResolution is null, do nothing
    const resolution = realTimeChatCountStatus?.liveChatCountsResolution;
    if (!resolution) {
      return;
    }

    // If LOW resolution
    if (resolution === RealtimeSearchCountResolution.LOW) {
      if (searchTermRef.current && searchTermRef.current.length > 0) {
        return; // search term exists, do nothing
      }
      // else, start timeout
    }

    // If HIGH resolution, always start timeout
    timeoutRef.current = setTimeout(async () => {
      apiInProgressRef.current = true;
      try {
        // Use the latest searchTerm value
        await refreshFilterCounts();
      } finally {
        apiInProgressRef.current = false;
        setSocketEventReceived(false); // reset flag, wait for next socket event
        timeoutRef.current = null;
      }
    }, liveChatCountsRefreshInterval);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socketEventReceived, realTimeChatCountStatus, refreshFilterCounts]);

  const { data: filterCounts } = useAtomValue(chatFilterStatusCountsAtom);
  const hasAnyCountUnder30 = (filterCounts ?? []).some((filterCount) =>
    filterCount.counts.some(({ type, count }) => type !== ChatStatuses.ALL && Number(count) < 30)
  );

  // runs only while the tab is active and at least one count is under 100.
  const pollingEnabled = isTabVisible && hasAnyCountUnder30;

  useInfinitePolling(
    async () => {
      refreshFilterCounts();
    },
    {
      interval: POLLING_INTERVAL,
      enabled: pollingEnabled,
      immediate: false,
      maxRetries: Infinity,
    }
  );
};

export default useUpdateFilterCounts;
